// GroupSettings Component - Group administration panel

import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft,
  Camera,
  Users,
  UserPlus,
  Shield,
  Crown,
  User,
  Search,
  X,
  Check,
  Loader2,
  Mail,
  ChevronDown,
  Trash2,
  Save,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getGroup,
  updateGroup,
  deleteGroup,
  getGroupMembers,
  updateMemberRole,
  removeMember,
  inviteUser,
  getJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from '../../services/groupsService';
import { uploadGroupImage } from '../../services/storageService';
import { collection, query, where, getDocs, limit } from '@firebase/firestore';
import { db } from '../../services/firebase';
import type { Group, GroupMember, GroupMemberRole, GroupJoinRequest } from '../../types/groups';

interface GroupSettingsProps {
  groupId: string;
  onBack: () => void;
  onGroupUpdated?: () => void;
  onGroupDeleted?: () => void;
}

export function GroupSettings({ groupId, onBack, onGroupUpdated, onGroupDeleted }: GroupSettingsProps) {
  const { user } = useAuth();
  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [joinRequests, setJoinRequests] = useState<GroupJoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'general' | 'members' | 'invites' | 'requests'>('general');

  // Image upload state
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Invite state
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteResults, setInviteResults] = useState<{ id: string; displayName: string; photoURL?: string }[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [inviting, setInviting] = useState<string | null>(null);

  // Role change state
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Delete state
  const [deleting, setDeleting] = useState(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [groupData, membersData, requestsData] = await Promise.all([
          getGroup(groupId),
          getGroupMembers(groupId),
          getJoinRequests(groupId).catch(() => []),
        ]);
        setGroup(groupData);
        setMembers(membersData);
        setJoinRequests(requestsData);
      } catch (err) {
        console.error('Error loading group settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [groupId]);

  // Search users for invite
  useEffect(() => {
    if (inviteSearch.trim().length < 2) {
      setInviteResults([]);
      return;
    }

    const searchUsers = async () => {
      setSearchingUsers(true);
      try {
        // Search in userProfiles collection
        const searchLower = inviteSearch.toLowerCase();
        const q = query(
          collection(db, 'userProfiles'),
          where('privacy.searchable', '==', true),
          limit(20)
        );
        const snapshot = await getDocs(q);

        const results = snapshot.docs
          .map(doc => ({
            id: doc.id,
            displayName: doc.data().displayName || 'Anonym',
            photoURL: doc.data().photoURL,
          }))
          .filter(u =>
            u.displayName.toLowerCase().includes(searchLower) &&
            !members.some(m => m.userId === u.id)
          )
          .slice(0, 5);

        setInviteResults(results);
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        setSearchingUsers(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [inviteSearch, members]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !group) {
      console.error('[GroupSettings] No file or group:', { file, group });
      return;
    }

    console.log('[GroupSettings] Starting upload:', { fileName: file.name, fileSize: file.size, fileType: file.type });
    setUploadingCover(true);
    try {
      console.log('[GroupSettings] Uploading image...');
      const result = await uploadGroupImage(file, groupId, 'cover');
      console.log('[GroupSettings] Upload successful:', result);

      console.log('[GroupSettings] Updating group with new cover URL...');
      await updateGroup(groupId, { coverImageUrl: result.url });
      console.log('[GroupSettings] Group updated successfully');

      setGroup({ ...group, coverImageUrl: result.url });
      onGroupUpdated?.();
    } catch (err: any) {
      console.error('[GroupSettings] Upload error:', err);
      alert(err.message || 'Fehler beim Hochladen');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleInvite = async (targetUser: { id: string; displayName: string }) => {
    if (!user || !group) return;

    setInviting(targetUser.id);
    try {
      await inviteUser(
        groupId,
        group.name,
        user.uid,
        user.displayName || 'Anonym',
        targetUser.id,
        targetUser.displayName
      );
      setInviteSearch('');
      setInviteResults([]);
      alert(`Einladung an ${targetUser.displayName} gesendet!`);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Einladen');
    } finally {
      setInviting(null);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: GroupMemberRole) => {
    setChangingRole(memberId);
    try {
      await updateMemberRole(groupId, memberId, newRole);
      setMembers(members.map(m =>
        m.userId === memberId ? { ...m, role: newRole } : m
      ));
    } catch (err: any) {
      alert(err.message || 'Fehler beim Aendern der Rolle');
    } finally {
      setChangingRole(null);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`${memberName} wirklich aus der Gruppe entfernen?`)) return;

    try {
      await removeMember(groupId, memberId);
      setMembers(members.filter(m => m.userId !== memberId));
    } catch (err: any) {
      alert(err.message || 'Fehler beim Entfernen');
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    if (!user) return;
    try {
      await approveJoinRequest(groupId, requestId, user.uid);
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
      // Reload members
      const updatedMembers = await getGroupMembers(groupId);
      setMembers(updatedMembers);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Genehmigen');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    if (!user) return;
    try {
      await rejectJoinRequest(groupId, requestId, user.uid);
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
    } catch (err: any) {
      alert(err.message || 'Fehler beim Ablehnen');
    }
  };

  const handleDeleteGroup = async () => {
    if (!group) return;

    const confirmText = `Bist du sicher, dass du "${group.name}" loeschen moechtest? Diese Aktion kann nicht rueckgaengig gemacht werden.`;
    if (!confirm(confirmText)) return;

    setDeleting(true);
    try {
      await deleteGroup(groupId);
      alert('Gruppe wurde geloescht');
      onGroupDeleted?.();
      onBack();
    } catch (err: any) {
      alert(err.message || 'Fehler beim Loeschen der Gruppe');
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadge = (role: GroupMemberRole) => {
    switch (role) {
      case 'creator':
        return { icon: Crown, label: 'Gruender', color: 'text-yellow-600 bg-yellow-100' };
      case 'admin':
        return { icon: Shield, label: 'Admin', color: 'text-purple-600 bg-purple-100' };
      case 'moderator':
        return { icon: Shield, label: 'Moderator', color: 'text-blue-600 bg-blue-100' };
      default:
        return { icon: User, label: 'Mitglied', color: 'text-gray-600 bg-gray-100' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600">
          <ArrowLeft className="w-5 h-5" />
          Zurueck
        </button>
        <p className="mt-8 text-center text-gray-500">Gruppe nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gruppeneinstellungen</h1>
              <p className="text-sm text-gray-500">{group.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex gap-1 bg-white rounded-lg border border-gray-200 p-1">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'general' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Allgemein
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'members' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Mitglieder
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'invites' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Einladen
          </button>
          {joinRequests.length > 0 && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors relative ${
                activeTab === 'requests' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Anfragen
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {joinRequests.length}
              </span>
            </button>
          )}
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pb-8">
        {/* General Tab - Cover Image Upload */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* Cover Image */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Titelbild</h3>
              </div>
              <div className="p-4">
                <div className="relative h-48 bg-gray-100 rounded-lg overflow-hidden">
                  {group.coverImageUrl ? (
                    <img
                      src={group.coverImageUrl}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                  <button
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploadingCover}
                    className="absolute bottom-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 shadow-lg"
                  >
                    {uploadingCover ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5" />
                    )}
                    {group.coverImageUrl ? 'Aendern' : 'Hochladen'}
                  </button>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className="hidden"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Empfohlen: 1920x480 Pixel, max. 5MB
                </p>
              </div>
            </div>

            {/* Danger Zone - Delete Group (for creator and admins) */}
            {['creator', 'admin'].includes(members.find(m => m.userId === user?.uid)?.role || '') && (
              <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-red-100 bg-red-50">
                  <h3 className="font-semibold text-red-700">Gefahrenzone</h3>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Wenn du diese Gruppe loeschst, werden alle Beitraege, Events und Mitgliedschaften
                    endgueltig entfernt. Diese Aktion kann nicht rueckgaengig gemacht werden.
                  </p>
                  <button
                    onClick={handleDeleteGroup}
                    disabled={deleting}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Gruppe loeschen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Members Tab - Role Management */}
        {activeTab === 'members' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {members.length} Mitglieder verwalten
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {members.map((member) => {
                const badge = getRoleBadge(member.role);
                const canModify = member.role !== 'creator' && user?.uid !== member.userId;

                return (
                  <div key={member.id} className="flex items-center gap-3 px-4 py-3">
                    {member.photoURL ? (
                      <img
                        src={member.photoURL}
                        alt={member.displayName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{member.displayName}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${badge.color}`}>
                        <badge.icon className="w-3 h-3" />
                        {badge.label}
                      </span>
                    </div>

                    {canModify && (
                      <div className="flex items-center gap-2">
                        {/* Role Dropdown */}
                        <div className="relative">
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.userId, e.target.value as GroupMemberRole)}
                            disabled={changingRole === member.userId}
                            className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            <option value="member">Mitglied</option>
                            <option value="moderator">Moderator</option>
                            <option value="admin">Admin</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveMember(member.userId, member.displayName)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Entfernen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Mitglieder einladen</h3>
            </div>
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  placeholder="Benutzer suchen..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {searchingUsers && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                )}
              </div>

              {inviteResults.length > 0 && (
                <div className="mt-4 space-y-2">
                  {inviteResults.map((result) => (
                    <div
                      key={result.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      {result.photoURL ? (
                        <img
                          src={result.photoURL}
                          alt={result.displayName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <span className="flex-1 font-medium text-gray-900">
                        {result.displayName}
                      </span>
                      <button
                        onClick={() => handleInvite(result)}
                        disabled={inviting === result.id}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                      >
                        {inviting === result.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        Einladen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {inviteSearch.length >= 2 && inviteResults.length === 0 && !searchingUsers && (
                <p className="mt-4 text-center text-gray-500">
                  Keine Benutzer gefunden
                </p>
              )}

              <p className="text-sm text-gray-500 mt-4">
                Suche nach Benutzern, die bereits ein Profil haben. Eingeladene Benutzer erhalten eine Benachrichtigung.
              </p>
            </div>
          </div>
        )}

        {/* Join Requests Tab */}
        {activeTab === 'requests' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {joinRequests.length} Beitrittsanfragen
              </h3>
            </div>
            {joinRequests.length === 0 ? (
              <div className="p-8 text-center">
                <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Keine offenen Anfragen</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {joinRequests.map((request) => (
                  <div key={request.id} className="flex items-center gap-3 px-4 py-3">
                    {request.photoURL ? (
                      <img
                        src={request.photoURL}
                        alt={request.displayName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{request.displayName}</p>
                      {request.message && (
                        <p className="text-sm text-gray-500 truncate">{request.message}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApproveRequest(request.id)}
                        className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                        title="Genehmigen"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleRejectRequest(request.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Ablehnen"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default GroupSettings;
